import {
  DefaultSchoolYear,
  FilterKeys,
  FilterSection,
  GetMessagesQueryFilters,
  MESSAGE_ARRANGE_BY_FILTER_KEYS,
  MessageArrangeBy,
  MESSAGES_FILTER_KEYS,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import {
  ArrangeByDropdown,
  DateRangeDropdown,
  FilterDropdown,
  FiltersContainer,
  FiltersSidebarMethods,
  MessageStatusExpandedSelect,
  MessageStatusTagSelect,
  MessageTypeExpandedSelect,
  MessageTypeTagSelect,
  PersonalFiltersDropdown,
  pickOnlyParamsFromFilterKeys,
  UserExpandedSelect,
  UserTagSelect,
} from '@schooly/components/filters';
import { PROPERTIES_TEXT_IDS } from '@schooly/constants';
import { forwardRef, useCallback, useImperativeHandle, useRef } from 'react';

import { MessagesFiltersSidebar } from './MessagesFiltersSidebar';
import { useMessagesFilters } from './useMessagesFilters';

type MessagesFiltersProps = {
  schoolId: string;
  filters: GetMessagesQueryFilters;
  defaultFilters: GetMessagesQueryFilters;
  onSetFilters: (v: GetMessagesQueryFilters) => void;
  notActualInitialDate?: boolean;
  defaultSchoolYear?: DefaultSchoolYear;
  arrangeBy: MessageArrangeBy | null;
  onSetArrangeBy: (v: MessageArrangeBy | null) => void;
  defaultUserFilters: GetMessagesQueryFilters;
  defaultUserArrangeBy: MessageArrangeBy | null;
};

export type MessageFilters = {
  openArrangeBy: () => void;
};

const MessagesFiltersComponent = (
  {
    arrangeBy,
    defaultFilters,
    filters,
    schoolId,
    onSetFilters,
    onSetArrangeBy,
    defaultSchoolYear,
    defaultUserFilters,
    defaultUserArrangeBy,
    notActualInitialDate,
  }: MessagesFiltersProps,
  ref: React.ForwardedRef<MessageFilters>,
) => {
  const sidebar = useRef<FiltersSidebarMethods | null>(null);
  const personalFiltersDropdown = useRef<PersonalFiltersDropdown | null>(null);
  const { currentStaff } = useAuth();

  const arrangeByDropdown = useRef<ArrangeByDropdown | null>(null);
  const {
    labels,
    filtersDate,
    filtersStatus,
    filtersType,
    filtersCreator,
    onSetDate,
    onSetMessageStatus,
    onClearMessageStatus,
    onSetMessageType,
    onClearMessageType,
    onSetCreator,
    onClearCreator,
  } = useMessagesFilters({ schoolId, filters, onSetFilters });

  useImperativeHandle(
    ref,
    () => ({
      openArrangeBy: () => {
        setTimeout(() => arrangeByDropdown.current?.open(), 300);
      },
    }),
    [],
  );

  const handleSaveFilter = useCallback(() => {
    personalFiltersDropdown.current?.saveFilter();
  }, []);

  return (
    <FiltersContainer onOpenFilters={() => sidebar.current?.open()}>
      <PersonalFiltersDropdown
        ref={personalFiltersDropdown}
        currentUser={currentStaff}
        relationId={currentStaff?.relation_id || ''}
        onSaveFilter={handleSaveFilter}
        schoolId={schoolId}
        section={FilterSection.Message}
        filters={{ ...filters, arrange_by: arrangeBy ?? undefined }}
        defaultSchoolYear={defaultSchoolYear}
        onSetFilters={(v) => {
          const appliedFilters = pickOnlyParamsFromFilterKeys(MESSAGES_FILTER_KEYS, v);

          onSetFilters({
            ...appliedFilters,
            [FilterKeys.Date]: appliedFilters.date || defaultFilters.date,
          });

          if (!v.arrange_by) {
            onSetArrangeBy(null);
            return;
          }
          for (const key of MESSAGE_ARRANGE_BY_FILTER_KEYS) {
            key === v.arrange_by && onSetArrangeBy(key);
          }
        }}
        getOptionLabelId={(o) =>
          o === FilterKeys.Creator ? 'messages-Sender' : PROPERTIES_TEXT_IDS[o]
        }
      />

      {filtersDate && (
        <DateRangeDropdown
          schoolId={schoolId}
          date={filtersDate}
          onSetDate={onSetDate}
          openLabel={labels[FilterKeys.Date]}
          defaultSchoolYear={defaultSchoolYear}
          notActualInitialDate={notActualInitialDate}
        />
      )}

      {!!filtersStatus?.length && (
        <FilterDropdown
          onClear={onClearMessageStatus}
          label={labels[FilterKeys.MessagesStatus]}
          tags={(open) =>
            filtersStatus.map((status) => (
              <MessageStatusTagSelect key={status} status={status} onClick={open} />
            ))
          }
        >
          {(onClose) => (
            <MessageStatusExpandedSelect
              selectedStatuses={filtersStatus}
              onApply={onSetMessageStatus}
              onClose={onClose}
              onClear={onClearMessageStatus}
            />
          )}
        </FilterDropdown>
      )}

      {!!filtersType?.length && (
        <FilterDropdown
          onClear={onClearMessageType}
          label={labels[FilterKeys.MessagesType]}
          tags={(open) =>
            filtersType.map((type) => (
              <MessageTypeTagSelect key={type} type={type} onClick={open} />
            ))
          }
        >
          {(onClose) => (
            <MessageTypeExpandedSelect
              selectedTypes={filtersType}
              onApply={onSetMessageType}
              onClose={onClose}
              onClear={onClearMessageType}
            />
          )}
        </FilterDropdown>
      )}

      {!!filtersCreator?.length && (
        <FilterDropdown
          onClear={onClearCreator}
          label={labels[FilterKeys.Creator]}
          tags={(open) =>
            filtersCreator.map((id) => (
              <UserTagSelect
                userType="staff"
                currentUser={currentStaff}
                sx={{ maxWidth: 200 }}
                schoolId={schoolId}
                key={id}
                id={id}
                onClick={open}
              />
            ))
          }
        >
          {(onClose) => (
            <UserExpandedSelect
              type="staff"
              schoolId={schoolId}
              currentUser={currentStaff}
              selectedIds={filtersCreator}
              onApply={onSetCreator}
              onClose={onClose}
              onClear={onClearCreator}
              filters={filters}
            />
          )}
        </FilterDropdown>
      )}

      {arrangeBy && (
        <ArrangeByDropdown
          ref={(r: any) => (arrangeByDropdown.current = r as ArrangeByDropdown)}
          onSelectOption={onSetArrangeBy}
          selectedOption={arrangeBy}
          options={MESSAGE_ARRANGE_BY_FILTER_KEYS}
          onClear={() => onSetArrangeBy(null)}
          onRemove={() => onSetArrangeBy(null)}
          getOptionLabelId={(o) =>
            o === FilterKeys.Creator ? 'messages-Sender' : PROPERTIES_TEXT_IDS[o]
          }
        />
      )}

      <MessagesFiltersSidebar
        ref={sidebar}
        defaultSchoolYear={defaultSchoolYear}
        defaultFilters={defaultFilters}
        filters={filters}
        onApply={(v) => {
          onSetFilters(v.filters);
          onSetArrangeBy(v.arrangeBy);
        }}
        arrangeBy={arrangeBy}
        schoolId={schoolId}
      />
    </FiltersContainer>
  );
};

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const MessagesFilters = forwardRef(MessagesFiltersComponent);
